import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Package,
  ClipboardCheck,
  AlertTriangle,
  Clock,
  Plus,
  Search,
  CheckCircle,
  Eye,
  Truck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  goodsReceiptApi,
  GoodsReceipt as GoodsReceiptType,
  GoodsReceiptDashboard,
  PendingPO,
  ReceiveItemDto,
} from '@/api/services/goods-receipt.api';
import { locationsApi, Location } from '@/api/services/locations.api';

const statusColors: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  PARTIALLY_RECEIVED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  COMPLETED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  CANCELLED: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
};

const qualityColors: Record<string, string> = {
  PENDING_INSPECTION: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  PASSED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  CONDITIONAL: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};

const severityColors: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  MEDIUM: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  HIGH: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  CRITICAL: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const discrepancyStatusColors: Record<string, string> = {
  OPEN: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  IN_PROGRESS: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  RESOLVED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  CLOSED: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
};

export default function GoodsReceipt() {
  const [receipts, setReceipts] = useState<GoodsReceiptType[]>([]);
  const [pendingPOs, setPendingPOs] = useState<PendingPO[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [dashboard, setDashboard] = useState<GoodsReceiptDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState<GoodsReceiptType | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [discrepancyModalOpen, setDiscrepancyModalOpen] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Form state
  const [selectedPO, setSelectedPO] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [carrier, setCarrier] = useState('');
  const [notes, setNotes] = useState('');
  const [receiveItems, setReceiveItems] = useState<Record<string, ReceiveItemDto>>({});
  const [discrepancyType, setDiscrepancyType] = useState<string>('QUANTITY_MISMATCH');
  const [discrepancyDescription, setDiscrepancyDescription] = useState('');
  const [discrepancySeverity, setDiscrepancySeverity] = useState<string>('MEDIUM');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [receiptsRes, dashboardRes, pendingRes, locationsRes] = await Promise.all([
        goodsReceiptApi.getAll({ limit: 50 }),
        goodsReceiptApi.getDashboard(),
        goodsReceiptApi.getPendingPOs(),
        locationsApi.getAll({ limit: 100 }),
      ]);
      setReceipts(receiptsRes.data);
      setDashboard(dashboardRes);
      setPendingPOs(pendingRes);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load goods receipt data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReceipt = async () => {
    if (!selectedPO || !selectedLocation) {
      toast.error('Please select a PO and location');
      return;
    }

    try {
      await goodsReceiptApi.createFromPO({
        purchaseOrderId: selectedPO,
        locationId: selectedLocation,
        deliveryNoteNumber: deliveryNote || undefined,
        carrierName: carrier || undefined,
        notes: notes || undefined,
      });
      toast.success('Goods receipt created');
      setCreateModalOpen(false);
      resetCreateForm();
      loadData();
    } catch (error) {
      toast.error('Failed to create goods receipt');
    }
  };

  const handleReceiveItems = async () => {
    if (!selectedReceipt) return;

    const items = Object.values(receiveItems).filter(item => item.receivedQty > 0);
    if (items.length === 0) {
      toast.error('Please enter quantities to receive');
      return;
    }

    try {
      await goodsReceiptApi.receiveItems(selectedReceipt.id, items);
      toast.success('Items received successfully');
      setReceiveModalOpen(false);
      setReceiveItems({});
      // Reload the receipt detail
      const updated = await goodsReceiptApi.getById(selectedReceipt.id);
      setSelectedReceipt(updated);
      loadData();
    } catch (error) {
      toast.error('Failed to receive items');
    }
  };

  const handleReportDiscrepancy = async () => {
    if (!selectedReceipt || !discrepancyDescription) {
      toast.error('Please provide discrepancy details');
      return;
    }

    try {
      await goodsReceiptApi.reportDiscrepancy(selectedReceipt.id, {
        type: discrepancyType as any,
        description: discrepancyDescription,
        severity: discrepancySeverity as any,
      });
      toast.success('Discrepancy reported');
      setDiscrepancyModalOpen(false);
      setDiscrepancyDescription('');
      // Reload receipt detail
      const updated = await goodsReceiptApi.getById(selectedReceipt.id);
      setSelectedReceipt(updated);
      loadData();
    } catch (error) {
      toast.error('Failed to report discrepancy');
    }
  };

  const handleViewReceipt = async (receipt: GoodsReceiptType) => {
    try {
      const detailed = await goodsReceiptApi.getById(receipt.id);
      setSelectedReceipt(detailed);
      setDetailSheetOpen(true);
    } catch (error) {
      toast.error('Failed to load receipt details');
    }
  };

  const initReceiveItems = () => {
    if (!selectedReceipt?.items) return;
    const itemsMap: Record<string, ReceiveItemDto> = {};
    selectedReceipt.items.forEach((item) => {
      itemsMap[item.id] = {
        goodsReceiptItemId: item.id,
        receivedQty: 0,
        qualityStatus: 'PASSED',
      };
    });
    setReceiveItems(itemsMap);
    setReceiveModalOpen(true);
  };

  const resetCreateForm = () => {
    setSelectedPO('');
    setSelectedLocation('');
    setDeliveryNote('');
    setCarrier('');
    setNotes('');
  };

  const filteredReceipts = receipts.filter((receipt) => {
    const matchesSearch =
      receipt.receiptNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt.purchaseOrder.poNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || receipt.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Goods Receipt</h1>
          <p className="text-muted-foreground">Receive and inspect incoming goods</p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Receipt
        </Button>
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Pending</p>
                  <p className="text-3xl font-semibold">{dashboard.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Partial</p>
                  <p className="text-3xl font-semibold">{dashboard.partial}</p>
                </div>
                <Package className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Completed Today</p>
                  <p className="text-3xl font-semibold">{dashboard.completedToday}</p>
                </div>
                <ClipboardCheck className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Open Discrepancies</p>
                  <p className="text-3xl font-semibold">{dashboard.openDiscrepancies}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="receipts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="receipts">Goods Receipts</TabsTrigger>
          <TabsTrigger value="pending">Pending POs ({pendingPOs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="receipts" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by receipt or PO number..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="PARTIALLY_RECEIVED">Partial</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Receipts Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Receipt #</TableHead>
                  <TableHead>PO #</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Received By</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Received At</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReceipts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      No goods receipts found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReceipts.map((receipt) => (
                    <TableRow key={receipt.id}>
                      <TableCell className="font-medium">{receipt.receiptNumber}</TableCell>
                      <TableCell>{receipt.purchaseOrder.poNumber}</TableCell>
                      <TableCell>{receipt.location.name}</TableCell>
                      <TableCell>{receipt.receivedBy.fullName}</TableCell>
                      <TableCell>
                        {receipt._count?.items || 0}
                        {receipt._count?.discrepancies ? (
                          <Badge variant="outline" className="ml-2 text-red-600">
                            {receipt._count.discrepancies} issues
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[receipt.status]}>
                          {receipt.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>{format(new Date(receipt.receivedAt), 'MMM d, yyyy HH:mm')}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => handleViewReceipt(receipt)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="space-y-4">
          {/* Pending POs Table */}
          <Card>
            <CardHeader>
              <CardTitle>Purchase Orders Awaiting Receipt</CardTitle>
              <CardDescription>Select a PO to create a new goods receipt</CardDescription>
            </CardHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO #</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Expected Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingPOs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No pending purchase orders
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingPOs.map((po) => (
                    <TableRow key={po.id}>
                      <TableCell className="font-medium">{po.poNumber}</TableCell>
                      <TableCell>{po.supplier.name}</TableCell>
                      <TableCell>{po.items.length} items</TableCell>
                      <TableCell>
                        {po.expectedDate ? format(new Date(po.expectedDate), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        {po.totalAmount ? `$${po.totalAmount.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[po.status] || statusColors.PENDING}>
                          {po.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedPO(po.id);
                            setCreateModalOpen(true);
                          }}
                        >
                          <Truck className="mr-1 h-3 w-3" />
                          Receive
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Receipt Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Goods Receipt</DialogTitle>
            <DialogDescription>Create a new goods receipt from a purchase order</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Purchase Order *</Label>
              <Select value={selectedPO} onValueChange={setSelectedPO}>
                <SelectTrigger>
                  <SelectValue placeholder="Select PO" />
                </SelectTrigger>
                <SelectContent>
                  {pendingPOs.map((po) => (
                    <SelectItem key={po.id} value={po.id}>
                      {po.poNumber} - {po.supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Receiving Location *</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Delivery Note Number</Label>
              <Input
                value={deliveryNote}
                onChange={(e) => setDeliveryNote(e.target.value)}
                placeholder="Enter delivery note number"
              />
            </div>
            <div className="space-y-2">
              <Label>Carrier Name</Label>
              <Input
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                placeholder="Enter carrier name"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateReceipt}>Create Receipt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          {selectedReceipt && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <SheetTitle>{selectedReceipt.receiptNumber}</SheetTitle>
                  <Badge className={statusColors[selectedReceipt.status]}>
                    {selectedReceipt.status.replace('_', ' ')}
                  </Badge>
                </div>
                <SheetDescription>PO: {selectedReceipt.purchaseOrder.poNumber}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Receipt Info */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Location:</span>
                        <p className="font-medium">{selectedReceipt.location.name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Received By:</span>
                        <p className="font-medium">{selectedReceipt.receivedBy.fullName}</p>
                      </div>
                      {selectedReceipt.deliveryNoteNumber && (
                        <div>
                          <span className="text-muted-foreground">Delivery Note:</span>
                          <p className="font-medium">{selectedReceipt.deliveryNoteNumber}</p>
                        </div>
                      )}
                      {selectedReceipt.carrierName && (
                        <div>
                          <span className="text-muted-foreground">Carrier:</span>
                          <p className="font-medium">{selectedReceipt.carrierName}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Actions */}
                {selectedReceipt.status !== 'COMPLETED' && selectedReceipt.status !== 'CANCELLED' && (
                  <div className="flex gap-2">
                    <Button onClick={initReceiveItems}>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Receive Items
                    </Button>
                    <Button variant="outline" onClick={() => setDiscrepancyModalOpen(true)}>
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Report Issue
                    </Button>
                  </div>
                )}

                {/* Items */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Items</CardTitle>
                  </CardHeader>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Expected</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Accepted</TableHead>
                        <TableHead>Quality</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReceipt.items?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.purchaseOrderItem.item.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {item.purchaseOrderItem.item.sku}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{item.expectedQty}</TableCell>
                          <TableCell className="text-right">{item.receivedQty}</TableCell>
                          <TableCell className="text-right">
                            {item.acceptedQty}
                            {item.rejectedQty > 0 && (
                              <span className="ml-1 text-red-600">(-{item.rejectedQty})</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={qualityColors[item.qualityStatus]}>
                              {item.qualityStatus.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>

                {/* Discrepancies */}
                {selectedReceipt.discrepancies && selectedReceipt.discrepancies.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Discrepancies</CardTitle>
                    </CardHeader>
                    <div className="px-6 pb-6 space-y-3">
                      {selectedReceipt.discrepancies.map((disc) => (
                        <div key={disc.id} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <Badge className={severityColors[disc.severity]}>{disc.severity}</Badge>
                            <Badge className={discrepancyStatusColors[disc.status]}>{disc.status}</Badge>
                          </div>
                          <p className="text-sm font-medium">{disc.type.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-muted-foreground">{disc.description}</p>
                          {disc.resolution && (
                            <p className="text-sm text-emerald-600 mt-2">Resolution: {disc.resolution}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Receive Items Modal */}
      <Dialog open={receiveModalOpen} onOpenChange={setReceiveModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Receive Items</DialogTitle>
            <DialogDescription>Enter quantities received for each item</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedReceipt?.items?.map((item) => (
              <div key={item.id} className="border rounded-lg p-4 space-y-3">
                <div>
                  <p className="font-medium">{item.purchaseOrderItem.item.name}</p>
                  <p className="text-sm text-muted-foreground">
                    SKU: {item.purchaseOrderItem.item.sku} | Expected: {item.expectedQty}
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Received Qty</Label>
                    <Input
                      type="number"
                      min="0"
                      value={receiveItems[item.id]?.receivedQty || 0}
                      onChange={(e) =>
                        setReceiveItems((prev) => ({
                          ...prev,
                          [item.id]: {
                            ...prev[item.id],
                            receivedQty: parseInt(e.target.value) || 0,
                          },
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Rejected Qty</Label>
                    <Input
                      type="number"
                      min="0"
                      value={receiveItems[item.id]?.rejectedQty || 0}
                      onChange={(e) =>
                        setReceiveItems((prev) => ({
                          ...prev,
                          [item.id]: {
                            ...prev[item.id],
                            rejectedQty: parseInt(e.target.value) || 0,
                          },
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Quality Status</Label>
                    <Select
                      value={receiveItems[item.id]?.qualityStatus || 'PASSED'}
                      onValueChange={(value) =>
                        setReceiveItems((prev) => ({
                          ...prev,
                          [item.id]: {
                            ...prev[item.id],
                            qualityStatus: value as any,
                          },
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PASSED">Passed</SelectItem>
                        <SelectItem value="FAILED">Failed</SelectItem>
                        <SelectItem value="CONDITIONAL">Conditional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Batch Number</Label>
                    <Input
                      value={receiveItems[item.id]?.batchNumber || ''}
                      onChange={(e) =>
                        setReceiveItems((prev) => ({
                          ...prev,
                          [item.id]: {
                            ...prev[item.id],
                            batchNumber: e.target.value,
                          },
                        }))
                      }
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Expiry Date</Label>
                    <Input
                      type="date"
                      value={receiveItems[item.id]?.expiryDate || ''}
                      onChange={(e) =>
                        setReceiveItems((prev) => ({
                          ...prev,
                          [item.id]: {
                            ...prev[item.id],
                            expiryDate: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReceiveItems}>Confirm Receipt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Discrepancy Modal */}
      <Dialog open={discrepancyModalOpen} onOpenChange={setDiscrepancyModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Discrepancy</DialogTitle>
            <DialogDescription>Report an issue with the goods receipt</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={discrepancyType} onValueChange={setDiscrepancyType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="QUANTITY_MISMATCH">Quantity Mismatch</SelectItem>
                  <SelectItem value="QUALITY_ISSUE">Quality Issue</SelectItem>
                  <SelectItem value="DAMAGED_GOODS">Damaged Goods</SelectItem>
                  <SelectItem value="WRONG_ITEM">Wrong Item</SelectItem>
                  <SelectItem value="MISSING_DOCUMENTATION">Missing Documentation</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select value={discrepancySeverity} onValueChange={setDiscrepancySeverity}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={discrepancyDescription}
                onChange={(e) => setDiscrepancyDescription(e.target.value)}
                placeholder="Describe the issue..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscrepancyModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReportDiscrepancy}>Report Issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
